import React from "react";
import _ from "lodash";
import numeral from "numeral";
import moment from "moment-timezone";
import NumberFormat from "../common/NumberFormat";
import ReactTooltip from "react-tooltip";
import UnitCardMiniSparkline from "./UnitCardMiniSparkline";

const METRIC = {
  ECPM_RATIO: "ecpm_ratio",
  STR_RATIO: "str_ratio",
  RPM_RATIO: "rpm_ratio",
  BO_IVT_RATIO: "bo_ivt_ratio",
};

class Avg7DaySplotline extends React.PureComponent {
  constructor(props) {
    super(props);

    const { data, dataKey, isPercentage = true, roundDecimal = 2 } = this.props;
    const sparklineData = _(data)
      .map((d) => {
        if (!d) {
          return null;
        }

        let value = 0;
        let tooltipData = null;
        if (dataKey === METRIC.ECPM_RATIO) {
          value = d.b_ecpm === 0 ? 0 : d.o_ecpm / d.b_ecpm;
          const diff = d.o_ecpm - d.b_ecpm;

          // date, value, b value, o value, diff
          tooltipData = [
            moment(d.date).format("YYYY-MM-DD ddd"),
            `${_.round(value * (isPercentage ? 100 : 1), 0)}${
              isPercentage ? "%" : ""
            }`,
            d.b_ecpm,
            d.o_ecpm,
            `(${diff >= 0 ? "+" : ""}${_.round(diff, 2)})`,
          ];
        } else if (dataKey === METRIC.STR_RATIO) {
          value = d.b_str === 0 ? 0 : d.o_str / d.b_str;
          const diff = d.o_str - d.b_str;

          // date, value, b value, o value, diff
          tooltipData = [
            moment(d.date).format("YYYY-MM-DD ddd"),
            `${_.round(value * (isPercentage ? 100 : 1), 0)}${
              isPercentage ? "%" : ""
            }`,
            `${_.round(d.b_str * 100, 2)}%`,
            `${_.round(d.o_str * 100, 2)}%`,
            `(${diff >= 0 ? "+" : ""}${_.round(diff * 100, 2)}%)`,
          ];
        } else if (dataKey === METRIC.RPM_RATIO) {
          value = d.b_rpm === 0 ? 0 : d.o_rpm / d.b_rpm;
          const diff = d.o_rpm - d.b_rpm;

          // date, value, b value, o value, diff
          tooltipData = [
            moment(d.date).format("YYYY-MM-DD ddd"),
            `${_.round(value * (isPercentage ? 100 : 1), 0)}${
              isPercentage ? "%" : ""
            }`,
            d.b_rpm,
            d.o_rpm,
            `(${diff >= 0 ? "+" : ""}${_.round(diff, 2)})`,
          ];
        } else if (dataKey === METRIC.BO_IVT_RATIO) {
          const bIvt = _.get(d, ["ivt", "ivt_ratio", "b"], 0);
          const oIvt = _.get(d, ["ivt", "ivt_ratio", "o"], 0);
          value = bIvt === 0 ? 0 : oIvt / bIvt;
          const diff = oIvt - bIvt;

          // date, value, b value, o value, diff
          tooltipData = [
            moment(d.date).format("YYYY-MM-DD ddd"),
            `${_.round(value * (isPercentage ? 100 : 1), 0)}${
              isPercentage ? "%" : ""
            }`,
            `${_.round(bIvt * 100, 2)}%`,
            `${_.round(oIvt * 100, 2)}%`,
            `(${diff >= 0 ? "+" : ""}${_.round(diff * 100, 2)}%)`,
          ];
        } else {
          value = d[dataKey];
        }

        return {
          date: d.date,
          value: _.round(value || 0, roundDecimal), //+ "%",
          tooltipData,
        };
      })
      .compact()
      .orderBy("date", "desc")
      .value();
    const avg = _.mean(_.map(sparklineData, "value"));

    this.state = {
      sparklineData,
      avg,

      hoveringMetric: null,
    };

    this.handleMouseEnterToPoint = this.handleMouseEnterToPoint.bind(this);
    this.handleMouseLeaveGraph = this.handleMouseLeaveGraph.bind(this);
  }

  handleMouseEnterToPoint(point, pointIndex) {
    // console.log(point, pointIndex);
    this.setState({
      hoveringMetric: this.state.sparklineData[pointIndex].value,
    });
  }

  handleMouseLeaveGraph() {
    this.setState({ hoveringMetric: null });
  }

  render() {
    const {
      title,
      subtitle,
      dataKey,
      unitId,
      description,
      isPercentage = true,
      hideSplotline = false,
    } = this.props;
    const { sparklineData, avg, hoveringMetric } = this.state;

    const tooltipId = `${unitId}_${title}_detail_tooltip`;

    const dataTable = `<table>${
      _.values(METRIC).includes(dataKey)
        ? `<tr>
      <td></td>
      <td></td>
      <td style='text-align: center;'>b</td>
      <td style='text-align: center;'>o</td>
      <td></td>
    </tr>`
        : ""
    }${sparklineData
      .map((d) => {
        if (_.isNil(d.tooltipData)) {
          let v = isPercentage
            ? _.round(d.value * 100, 2) + "%"
            : numeral(d.value).format("0,0");
          if (dataKey === "req") v = numeral(d.value).format("0,0.00a");
          return `<tr>
          <td>${moment(d.date).format("YYYY-MM-DD ddd")}</td>
          <td style='text-align: right; padding-left: 4px;'>${v}</td>
        </tr>`;
        } else {
          return `<tr>
          ${d.tooltipData
            .map((tdd, i) => {
              return `<td 
                  ${i > 0 && "style='text-align: right; padding-left: 4px;'"}
                >${tdd}</td>`;
            })
            .join("")}
         
        </tr>`;
        }
      })
      .join("")}</table>`;

    let format = isPercentage ? "0,0%" : "0,0";
    if (dataKey === "req") format = "0,0.00a";

    return (
      <div className="mr-1 inline-block">
        <div
          className={`rounded cursor-pointer bg-indigo-100 px-2 py-1 text-xs ${
            this.props.handleClick && "hover:bg-blue-200"
          } ${hideSplotline ? "flex items-center gap-1" : ""}`}
          data-tip
          data-for={tooltipId}
          onMouseEnter={() =>
            this.props.handleMouseEnter && this.props.handleMouseEnter()
          }
          onMouseLeave={() =>
            this.props.handleMouseLeave && this.props.handleMouseLeave()
          }
          onClick={() => {
            this.props.handleClick && this.props.handleClick();
          }}
        >
          <div className="flex gap-2">
            <small>
              {subtitle} <span className="font-semibold">{title}</span>
            </small>
          </div>
          <div className="flex items-center gap-2">
            <div
              className="text-right font-semibold"
              style={{ width: hideSplotline ? "" : "2.1rem" }}
            >
              {hoveringMetric ? (
                <NumberFormat
                  value={hoveringMetric}
                  format={format}
                ></NumberFormat>
              ) : (
                <NumberFormat value={avg} format={format}></NumberFormat>
              )}
            </div>
            {!hideSplotline && (
              <UnitCardMiniSparkline
                data={_.reverse(_.map(sparklineData, "value"))}
                width={title.includes("Net") ? 40 : undefined}
              ></UnitCardMiniSparkline>
            )}
          </div>
        </div>
        <ReactTooltip
          html={true}
          id={tooltipId}
          type="dark"
          effect="solid"
          place="top"
          getContent={() => {
            return `
                <div>
                  ${subtitle || ""} ${title || ""} (Avg: ${numeral(avg).format(
              format
            )})
                </div>
                ${
                  description
                    ? `<div className="text-xs">(${description})</div>`
                    : ""
                }
                ${dataTable}
                ${
                  this.props.handleClick
                    ? `<div className="text-xs">Click to load more data ...</div>`
                    : ""
                }
              `;
          }}
        ></ReactTooltip>
      </div>
    );
  }
}
export default Avg7DaySplotline;
